import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, map, shareReplay } from 'rxjs';

import { format } from 'date-fns';
import { makePermissionsComplete } from 'server/helpers/permissions/permissions';
import { AvailabilityStatusEnum } from 'server/shared/models/availabilityStatusEnum';
import { getObjectKeys } from 'server/shared/models/getObjectKeys';
import { RoleEnum } from 'server/shared/models/role.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import { UserAvailability } from 'server/shared/models/userAvailability';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { PermissionsService } from '../../services/permissions.service';
import { UserService } from '../../services/user.service';
import { ShiftToggleEvent } from '../modules/availability-shift-toggle-list/availability-shift-toggle-list.component';

@Component({
  selector: 'app-availability-day-item',
  templateUrl: 'availability-day-item.component.html',
  styleUrls: ['availability-day-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityDayItemComponent implements OnChanges {
  available?: boolean = false;
  unavailable?: boolean = false;
  working$ = new BehaviorSubject<boolean>(false);
  workingShifts$ = new BehaviorSubject<number[]>([]);
  shiftEnum = ShiftEnum;
  availabilityStatusEnum = AvailabilityStatusEnum;
  @Input() date?: Date;
  @Input() user?: PoolerUser;

  collapsed$ = new BehaviorSubject<boolean>(true);

  availabilityForToday$ = new BehaviorSubject<UserAvailability | undefined>(
    undefined,
  );

  availableShifts$ = new BehaviorSubject<ShiftEnum[] | undefined>(undefined);

  isEditingNote$ = new BehaviorSubject<boolean>(false);
  noteText$ = new BehaviorSubject<string | undefined>(undefined);

  canUpdateAvailabilities$ = this.userService.readyUser$.pipe(
    map((user: User) => {
      if (user.role === RoleEnum.DISPO) {
        let permissions = makePermissionsComplete(user.dispoPermissions);
        return permissions.poolerAccess.update;
      } else {
        return user._id === this.user?._id;
      }
    }),
    shareReplay(1),
  );

  constructor(
    private ngZone: NgZone,
    public permissionsService: PermissionsService,
    private userService: UserService,
  ) {}

  ngOnInit() {
    if (!this.date) {
      return;
    }
    this._setStatus();
    this.noteText$.next(this.availabilityForToday$.value?.note);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.user) {
      this._setStatus();
    }
  }

  editNote(event: Event) {
    event.stopPropagation();
    this.isEditingNote$.next(true);
  }

  saveNote(event: Event) {
    event.stopPropagation();
    const availabilityForToday = this.availabilityForToday$.value;
    if (!availabilityForToday) {
      return;
    }
    this.isEditingNote$.next(false);
    availabilityForToday.note = this.noteText$.value;
    this.availabilityForToday$.next(availabilityForToday);
    this._updateUser();
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  private _setStatus() {
    if (!this.date) return;
    const availabilityForToday = (this.user?.availabilities
      ? this.user.availabilities.find(
          (availability) =>
            this.date && availability.date == format(this.date, dbDayDateToken),
        )
      : undefined) || {
      shifts: {},
      date: format(this.date, dbDayDateToken),
    };
    this.availabilityForToday$.next(availabilityForToday);
    this.available = availabilityForToday.isAvailable === true;
    this.unavailable = availabilityForToday.isAvailable === false;
    if (availabilityForToday && availabilityForToday.shifts) {
      const workingShiftKeys = Object.keys(availabilityForToday.shifts)
        .filter(
          (key) =>
            //  @ts-ignore
            availabilityForToday?.shifts?.[key] ==
            AvailabilityStatusEnum.WORKING,
        )
        .map((key) => parseInt(key, 10));
      if (workingShiftKeys) {
        this.workingShifts$.next(workingShiftKeys);
        if (workingShiftKeys && workingShiftKeys.length > 0) {
          this.working$.next(true);
        }
      }
    }

    this.updateAvailableShiftsArray();
  }

  get undecided(): boolean {
    return this.availabilityForToday$.value?.isAvailable == undefined;
  }

  public toggleAvailabilityState(available: boolean): void {
    const currentAvailability = this.availabilityForToday$.value;
    const originalAvailability = currentAvailability
      ? {
          ...currentAvailability,
          shifts: { ...currentAvailability?.shifts },
        }
      : undefined;
    if (available == undefined) {
      this.ngZone.run(() => {
        if (currentAvailability) {
          delete currentAvailability.isAvailable;
          currentAvailability.shifts = {};
          this.availabilityForToday$.next(currentAvailability);
        }
      });
      this._updateUser();
    } else {
      if (available) {
        this.setAvailable();
      } else {
        this.setUnavailable(originalAvailability);
      }
    }
    this.updateAvailableShiftsArray();
  }

  updateAvailableShiftsArray(): void {
    const availabilityForToday = this.availabilityForToday$.value;
    if (!availabilityForToday?.shifts) {
      return;
    }
    const availableShifts = getObjectKeys(availabilityForToday.shifts).filter(
      (key) => availabilityForToday?.shifts?.[key] === 'AVAILABLE',
    );
    this.availableShifts$.next(availableShifts);
  }

  public async toggleShift(toggleEvent: ShiftToggleEvent) {
    const availabilityForToday = this.availabilityForToday$.value;
    if (!availabilityForToday?.shifts) {
      return;
    }

    const originalShifts = { ...availabilityForToday.shifts };

    if (toggleEvent.available == undefined) {
      delete availabilityForToday.shifts[toggleEvent.shift];
    } else {
      availabilityForToday.shifts[toggleEvent.shift] = toggleEvent.available
        ? AvailabilityStatusEnum.AVAILABLE
        : AvailabilityStatusEnum.UNAVAILABLE;
    }

    this.availabilityForToday$.next(availabilityForToday);
    this.updateAvailabilityState();
    this.updateAvailableShiftsArray();

    const result = await this._updateUser();

    if (!result?.availabilityUpdated) {
      // Reset to original state if update failed
      availabilityForToday.shifts = originalShifts;
      this.availabilityForToday$.next(availabilityForToday);
      this.updateAvailabilityState();
      this.updateAvailableShiftsArray();
    }
  }

  updateAvailabilityState() {
    const availabilityForToday = this.availabilityForToday$.value;
    if (!availabilityForToday?.shifts) {
      return;
    }
    if (
      Object.keys(availabilityForToday.shifts).some(
        (key) =>
          //  @ts-ignore
          availabilityForToday?.shifts?.[key] ==
          AvailabilityStatusEnum.AVAILABLE,
      )
    ) {
      availabilityForToday.isAvailable = true;
      this.available = true;
      this.unavailable = false;
    } else if (!Object.keys(availabilityForToday.shifts).length) {
      delete availabilityForToday.isAvailable;
      delete this.available;
      delete this.unavailable;
    } else if (
      Object.keys(availabilityForToday.shifts).every(
        (key) =>
          //  @ts-ignore
          availabilityForToday?.shifts?.[key] ==
          AvailabilityStatusEnum.UNAVAILABLE,
      )
    ) {
      availabilityForToday.isAvailable = false;
      this.available = false;
      this.unavailable = true;
    }
    this.availabilityForToday$.next(availabilityForToday);
  }

  public setAvailable(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.ngZone.run(() => {
      const availabilityForToday = this.availabilityForToday$.value;
      if (!availabilityForToday) {
        return;
      }
      this.unavailable = false;
      this.available = true;
      availabilityForToday.isAvailable = true;
      this.availabilityForToday$.next(availabilityForToday);
    });
    const availabilityForToday = this.availabilityForToday$.value;
    Object.keys(ShiftEnum)
      .filter((key) => !isNaN(Number(key)))
      .forEach((shift) => {
        if (!availabilityForToday?.shifts) {
          return;
        }
        if (!this.user?.shifts || this.user.shifts.length == 0) {
          //  @ts-ignore
          availabilityForToday.shifts[shift] = 'AVAILABLE';
        } else if (
          this.user.shifts &&
          this.user.shifts.includes(Number.parseInt(shift, 10))
        ) {
          //  @ts-ignore
          availabilityForToday.shifts[shift] = 'AVAILABLE';
        } else {
          //  @ts-ignore
          availabilityForToday.shifts[shift] = 'UNAVAILABLE';
        }
      });
    this.availabilityForToday$.next(availabilityForToday);
    this._updateUser();
  }

  public setUnavailable(
    originalAvailability?: UserAvailability,
    event?: Event,
  ): void {
    if (event) {
      event.stopPropagation();
    }
    const originalState = {
      available: this.available,
      unavailable: this.unavailable,
      availabilityForToday: originalAvailability,
    };
    this.ngZone.run(() => {
      this.unavailable = true;
      this.available = false;
      const availabilityForToday = this.availabilityForToday$.value;
      if (!availabilityForToday) {
        return;
      }
      availabilityForToday.isAvailable = false;
      this.availabilityForToday$.next(availabilityForToday);
    });
    const availabilityForToday = this.availabilityForToday$.value;
    Object.keys(ShiftEnum)
      .filter((key) => !isNaN(Number(key)))
      .forEach((shift) => {
        if (!availabilityForToday?.shifts) {
          return;
        }
        //  @ts-ignore
        availabilityForToday.shifts[shift] = 'UNAVAILABLE';
      });
    this.availabilityForToday$.next(availabilityForToday);
    this._updateUser().then((result) => {
      if (!result?.availabilityUpdated) {
        this.ngZone.run(() => {
          this.available = originalState.available;
          this.unavailable = originalState.unavailable;
          this.availabilityForToday$.next(originalState.availabilityForToday);
        });
      }
    });
  }

  private async _updateUser() {
    if (!this.user) {
      return;
    }
    const availabilityForToday = this.availabilityForToday$.value;
    if (!availabilityForToday) {
      return;
    }

    return await this.userService.updateAvailability(
      this.user._id,
      availabilityForToday,
    );
  }

  public toggleCollapsed(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.collapsed$.next(!this.collapsed$.value);
  }
}
